/**
 * A finger- and mouse-friendly signature pad.
 *
 * Strokes are kept as point lists rather than as pixels. That makes three
 * things fall out for free: redrawing crisply after a resize or a
 * devicePixelRatio change, trimming the export to the inked bounding box, and
 * exporting on a transparent background while the on-screen pad still looks
 * like paper.
 */
import { MAX_SIGNATURE_BYTES } from './config';

export interface SignaturePadOptions {
  canvas: HTMLCanvasElement;
  /** Called whenever the pad flips between empty and inked. */
  onChange?: (isEmpty: boolean) => void;
}

export interface SignaturePad {
  clear(): void;
  isEmpty(): boolean;
  /** A trimmed transparent PNG data URL, or null when nothing was drawn. */
  toDataURL(): string | null;
  destroy(): void;
}

interface Point {
  x: number;
  y: number;
}

interface PendingStroke {
  pointerId: number;
  points: Point[];
  travelled: number;
  committed: boolean;
}

/** Ink colour. Fixed dark, because this lands on a white PDF page. */
const INK = '#101418';
/** Paper colour for the on-screen pad, in both colour schemes. */
const PAPER = '#ffffff';
const LINE_WIDTH = 2.4;
/**
 * How far a pointer must travel before it counts as a mark. A tap should leave
 * nothing behind and must not enable the Submit button.
 */
const INK_THRESHOLD_PX = 6;
/** Export scale, for a signature that stays sharp when the PDF is zoomed. */
const EXPORT_SCALE = 2;
const MAX_EXPORT_EDGE_PX = 1600;

export function createSignaturePad(options: SignaturePadOptions): SignaturePad {
  const { canvas, onChange } = options;
  const context = canvas.getContext('2d');
  if (!context) throw new Error('This browser cannot provide a drawing canvas.');
  const ctx = context;

  const strokes: Point[][] = [];
  let pending: PendingStroke | null = null;
  let lastReportedEmpty = true;
  /** The CSS box the stored points were drawn in, so a resize can follow it. */
  let box: { width: number; height: number } | null = null;

  /* ---------------------------------------------------------------------- */
  /* Painting                                                               */
  /* ---------------------------------------------------------------------- */

  function applyInkStyle(): void {
    ctx.strokeStyle = INK;
    ctx.lineWidth = LINE_WIDTH;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
  }

  function drawPolyline(target: CanvasRenderingContext2D, points: Point[]): void {
    if (points.length < 2) return;
    target.beginPath();
    points.forEach((point, index) => {
      if (index === 0) target.moveTo(point.x, point.y);
      else target.lineTo(point.x, point.y);
    });
    target.stroke();
  }

  function repaint(): void {
    const { width, height } = canvas.getBoundingClientRect();
    ctx.save();
    ctx.fillStyle = PAPER;
    ctx.fillRect(0, 0, width, height);
    ctx.restore();
    applyInkStyle();
    for (const stroke of strokes) drawPolyline(ctx, stroke);
  }

  /**
   * Move existing ink with the box when the box changes size - a phone rotating
   * mid-signature is the everyday case.
   *
   * The scale is uniform and the remainder is split evenly, so the signature
   * keeps its shape and stays centred instead of stretching or sliding out of
   * view. Points are rewritten rather than merely redrawn, so the exported PNG
   * always matches what the signer can see.
   */
  function reflow(width: number, height: number): void {
    if (!box || box.width === 0 || box.height === 0) return;
    if (width === box.width && height === box.height) return;
    const scale = Math.min(width / box.width, height / box.height);
    const shiftX = (width - box.width * scale) / 2;
    const shiftY = (height - box.height * scale) / 2;

    const move = (points: Point[]): void => {
      for (const point of points) {
        point.x = point.x * scale + shiftX;
        point.y = point.y * scale + shiftY;
      }
    };
    for (const stroke of strokes) move(stroke);
    // A committed stroke's points already live in `strokes`; only an
    // uncommitted one needs moving separately.
    if (pending && !pending.committed) {
      move(pending.points);
      pending.travelled *= scale;
    }
  }

  /**
   * Match the backing store to the CSS box times devicePixelRatio, so strokes
   * are sharp on retina screens instead of upscaled and soft.
   */
  function fit(): void {
    const rect = canvas.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) return;
    reflow(rect.width, rect.height);
    box = { width: rect.width, height: rect.height };
    const ratio = window.devicePixelRatio || 1;
    const width = Math.round(rect.width * ratio);
    const height = Math.round(rect.height * ratio);
    if (canvas.width !== width || canvas.height !== height) {
      canvas.width = width;
      canvas.height = height;
    }
    // Draw in CSS pixels; the transform handles the device ratio.
    ctx.setTransform(ratio, 0, 0, ratio, 0, 0);
    repaint();
  }

  /* ---------------------------------------------------------------------- */
  /* Input                                                                  */
  /* ---------------------------------------------------------------------- */

  function pointFrom(event: PointerEvent): Point {
    const rect = canvas.getBoundingClientRect();
    return { x: event.clientX - rect.left, y: event.clientY - rect.top };
  }

  function notifyChange(): void {
    const empty = strokes.length === 0;
    if (empty === lastReportedEmpty) return;
    lastReportedEmpty = empty;
    onChange?.(empty);
  }

  function onPointerDown(event: PointerEvent): void {
    if (pending !== null) return; // one pointer at a time
    if (event.button !== 0 && event.pointerType === 'mouse') return;
    event.preventDefault();
    // Capture so a stroke that wanders off the canvas still ends cleanly.
    try {
      canvas.setPointerCapture(event.pointerId);
    } catch {
      // Capture is a nicety; carry on without it.
    }
    pending = { pointerId: event.pointerId, points: [pointFrom(event)], travelled: 0, committed: false };
  }

  function onPointerMove(event: PointerEvent): void {
    const stroke = pending;
    if (!stroke || event.pointerId !== stroke.pointerId) return;
    event.preventDefault();

    // Coalesced events recover the full-rate samples a fast stroke produces.
    const samples =
      typeof event.getCoalescedEvents === 'function' ? event.getCoalescedEvents() : [event];
    const moves = samples.length > 0 ? samples : [event];

    for (const move of moves) {
      const next = pointFrom(move);
      const previous = stroke.points[stroke.points.length - 1];
      if (previous) {
        stroke.travelled += Math.hypot(next.x - previous.x, next.y - previous.y);
      }
      stroke.points.push(next);

      if (!stroke.committed && stroke.travelled >= INK_THRESHOLD_PX) {
        stroke.committed = true;
        strokes.push(stroke.points);
        applyInkStyle();
        drawPolyline(ctx, stroke.points);
        notifyChange();
        continue;
      }
      if (stroke.committed && previous) {
        applyInkStyle();
        drawPolyline(ctx, [previous, next]);
      }
    }
  }

  function endStroke(event: PointerEvent): void {
    const stroke = pending;
    if (!stroke || event.pointerId !== stroke.pointerId) return;
    try {
      if (canvas.hasPointerCapture(event.pointerId)) canvas.releasePointerCapture(event.pointerId);
    } catch {
      // Already released.
    }
    // An uncommitted stroke was a tap. It leaves no mark and no state.
    pending = null;
  }

  canvas.addEventListener('pointerdown', onPointerDown);
  canvas.addEventListener('pointermove', onPointerMove);
  canvas.addEventListener('pointerup', endStroke);
  canvas.addEventListener('pointercancel', endStroke);
  canvas.addEventListener('pointerleave', endStroke);

  const observer = new ResizeObserver(() => fit());
  observer.observe(canvas);
  fit();

  /* ---------------------------------------------------------------------- */
  /* Export                                                                 */
  /* ---------------------------------------------------------------------- */

  function inkBounds(): { minX: number; minY: number; maxX: number; maxY: number } | null {
    let minX = Number.POSITIVE_INFINITY;
    let minY = Number.POSITIVE_INFINITY;
    let maxX = Number.NEGATIVE_INFINITY;
    let maxY = Number.NEGATIVE_INFINITY;
    let seen = false;
    for (const stroke of strokes) {
      for (const point of stroke) {
        seen = true;
        if (point.x < minX) minX = point.x;
        if (point.y < minY) minY = point.y;
        if (point.x > maxX) maxX = point.x;
        if (point.y > maxY) maxY = point.y;
      }
    }
    if (!seen) return null;
    const pad = LINE_WIDTH * 2;
    return { minX: minX - pad, minY: minY - pad, maxX: maxX + pad, maxY: maxY + pad };
  }

  function render(scale: number): string | null {
    const bounds = inkBounds();
    if (!bounds) return null;
    const width = Math.max(1, bounds.maxX - bounds.minX);
    const height = Math.max(1, bounds.maxY - bounds.minY);
    const capped = Math.min(scale, MAX_EXPORT_EDGE_PX / Math.max(width, height));
    const effective = Math.max(1, capped);

    const out = document.createElement('canvas');
    out.width = Math.max(1, Math.round(width * effective));
    out.height = Math.max(1, Math.round(height * effective));
    const outCtx = out.getContext('2d');
    if (!outCtx) return null;

    // No background fill: a transparent PNG composites onto the PDF cleanly.
    outCtx.setTransform(effective, 0, 0, effective, -bounds.minX * effective, -bounds.minY * effective);
    outCtx.strokeStyle = INK;
    outCtx.lineWidth = LINE_WIDTH;
    outCtx.lineCap = 'round';
    outCtx.lineJoin = 'round';
    for (const stroke of strokes) drawPolyline(outCtx, stroke);

    return out.toDataURL('image/png');
  }

  /** Decoded byte length of a data URL's base64 payload. */
  function decodedBytes(dataUrl: string): number {
    const comma = dataUrl.indexOf(',');
    if (comma === -1) return 0;
    const payload = dataUrl.slice(comma + 1);
    const padding = payload.endsWith('==') ? 2 : payload.endsWith('=') ? 1 : 0;
    return Math.floor((payload.length * 3) / 4) - padding;
  }

  return {
    clear(): void {
      strokes.length = 0;
      pending = null;
      repaint();
      notifyChange();
    },

    isEmpty(): boolean {
      return strokes.length === 0;
    },

    toDataURL(): string | null {
      const preferred = render(EXPORT_SCALE);
      if (!preferred) return null;
      if (decodedBytes(preferred) <= MAX_SIGNATURE_BYTES) return preferred;
      // Vanishingly unlikely for line art, but the server rejects oversize PNGs
      // outright, so fall back to a smaller render rather than fail the signing.
      const smaller = render(1);
      if (smaller && decodedBytes(smaller) <= MAX_SIGNATURE_BYTES) return smaller;
      return null;
    },

    destroy(): void {
      observer.disconnect();
      canvas.removeEventListener('pointerdown', onPointerDown);
      canvas.removeEventListener('pointermove', onPointerMove);
      canvas.removeEventListener('pointerup', endStroke);
      canvas.removeEventListener('pointercancel', endStroke);
      canvas.removeEventListener('pointerleave', endStroke);
      pending = null;
      strokes.length = 0;
    },
  };
}
